import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Check, MessageCircle, Zap, Clock } from "lucide-react";
import heroComponents from "@/assets/optimized/hero-components.jpg";
import heroComponents480 from "@/assets/optimized/hero-components-480.webp";
import heroComponents640 from "@/assets/optimized/hero-components-640.webp";
import heroComponents720 from "@/assets/optimized/hero-components-720.webp";
import heroComponents800 from "@/assets/optimized/hero-components-800.webp";
import heroComponents480Jpg from "@/assets/optimized/hero-components-480.jpg";
import heroComponents640Jpg from "@/assets/optimized/hero-components-640.jpg";
import heroComponents720Jpg from "@/assets/optimized/hero-components-720.jpg";
import heroComponents800Jpg from "@/assets/optimized/hero-components-800.jpg";
import heroPackages from "@/assets/optimized/hero-packages.jpg";
import heroPackages480 from "@/assets/optimized/hero-packages-480.webp";
import heroPackages640 from "@/assets/optimized/hero-packages-640.webp";
import heroPackages720 from "@/assets/optimized/hero-packages-720.webp";
import heroPackages800 from "@/assets/optimized/hero-packages-800.webp";
import heroPackages480Jpg from "@/assets/optimized/hero-packages-480.jpg";
import heroPackages640Jpg from "@/assets/optimized/hero-packages-640.jpg";
import heroPackages720Jpg from "@/assets/optimized/hero-packages-720.jpg";
import heroPackages800Jpg from "@/assets/optimized/hero-packages-800.jpg";
import heroSystems from "@/assets/optimized/hero-systems.jpg";
import heroSystems480 from "@/assets/optimized/hero-systems-480.webp";
import heroSystems640 from "@/assets/optimized/hero-systems-640.webp";
import heroSystems720 from "@/assets/optimized/hero-systems-720.webp";
import heroSystems800 from "@/assets/optimized/hero-systems-800.webp";
import heroSystems480Jpg from "@/assets/optimized/hero-systems-480.jpg";
import heroSystems640Jpg from "@/assets/optimized/hero-systems-640.jpg";
import heroSystems720Jpg from "@/assets/optimized/hero-systems-720.jpg";
import heroSystems800Jpg from "@/assets/optimized/hero-systems-800.jpg";
import { useLang } from "@/i18n/LanguageContext";
import type { TKey } from "@/i18n/translations";
import { cn } from "@/lib/utils";
import { whatsappLinkProps } from "@/lib/whatsapp";
import { trackCtaClick } from "@/lib/analytics";

/**
 * Hero carousel — aligned with the 3-layer product architecture.
 * Slide order reflects strategic weighting: Components (core, LCP),
 * Packages (expansion), Systems (credibility).
 */
type Slide = {
  src: string;
  webpSrcSet: string;
  jpgSrcSet: string;
  altKey: TKey;
  captionKey: TKey;
};
const slides: Slide[] = [
  {
    src: heroComponents,
    webpSrcSet: `${heroComponents480} 480w, ${heroComponents640} 640w, ${heroComponents720} 720w, ${heroComponents800} 800w`,
    jpgSrcSet: `${heroComponents480Jpg} 480w, ${heroComponents640Jpg} 640w, ${heroComponents720Jpg} 720w, ${heroComponents800Jpg} 800w`,
    altKey: "hero.slide.components.alt",
    captionKey: "hero.slide.components.caption",
  },
  {
    src: heroPackages,
    webpSrcSet: `${heroPackages480} 480w, ${heroPackages640} 640w, ${heroPackages720} 720w, ${heroPackages800} 800w`,
    jpgSrcSet: `${heroPackages480Jpg} 480w, ${heroPackages640Jpg} 640w, ${heroPackages720Jpg} 720w, ${heroPackages800Jpg} 800w`,
    altKey: "hero.slide.packages.alt",
    captionKey: "hero.slide.packages.caption",
  },
  {
    src: heroSystems,
    webpSrcSet: `${heroSystems480} 480w, ${heroSystems640} 640w, ${heroSystems720} 720w, ${heroSystems800} 800w`,
    jpgSrcSet: `${heroSystems480Jpg} 480w, ${heroSystems640Jpg} 640w, ${heroSystems720Jpg} 720w, ${heroSystems800Jpg} 800w`,
    altKey: "hero.slide.systems.alt",
    captionKey: "hero.slide.systems.caption",
  },
];

// Mobile/tablet render the carousel at the full container width
// (~screen − 32px container padding); on lg+ the right column is roughly
// half the viewport, capped by the max container width. The previous
// `(min-width: 640px) 640px` clause forced the 800w source on tablets even
// though the displayed image is much smaller, wasting ~63 KiB per LCP.
const HERO_SIZES =
  "(min-width: 1024px) 640px, calc(100vw - 32px)";

const ROTATION_MS = 4500;

const Hero = () => {
  const { t, localePath } = useLang();
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;
    const id = window.setInterval(
      () => setActive((i) => (i + 1) % slides.length),
      ROTATION_MS,
    );
    return () => window.clearInterval(id);
  }, [paused]);

  const bulletKeys: TKey[] = [
    "hero.bullet1",
    "hero.bullet2",
    "hero.bullet3",
    "hero.bullet4",
    "hero.bullet5",
  ];

  return (
    <section className="relative overflow-hidden border-b border-border bg-background">
      <div className="container grid items-center gap-10 py-10 md:py-20 lg:grid-cols-2 lg:gap-16 lg:py-28">
        {/* Left: copy */}
        <div className="max-w-2xl">
          {/* LCP-critical: above-the-fold copy must NOT start at opacity:0.
              Removing the enter-up animation gate eliminates the 360-630ms
              animation-delay window that was sitting on top of the hero's
              already-large hydration cost. Visual result is identical
              after first paint; we just no longer hide it before that. */}
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-border bg-secondary px-3 py-1 text-xs font-medium text-secondary-foreground">
            <Zap className="h-3.5 w-3.5" />
            {t("hero.badge")}
          </div>

          <h1 className="text-[28px] font-bold leading-[1.15] tracking-tight text-foreground sm:text-4xl md:text-5xl lg:text-6xl">
            {t("hero.h1.part1")}{" "}
            <span className="text-primary">{t("hero.h1.part2")}</span>
          </h1>

          <p className="mt-4 text-base leading-relaxed text-muted-foreground sm:hidden">
            {t("hero.subtitle.mobile")}
          </p>
          <p className="mt-4 hidden text-base leading-relaxed text-muted-foreground sm:block sm:text-lg">
            {t("hero.subtitle")}
          </p>

          {/* CTAs — above the fold */}
          <div className="mt-6 flex flex-col gap-2.5 sm:flex-row">
            <Button asChild size="lg" className="h-12 w-full font-semibold sm:w-auto">
              <Link to={localePath("/request-quote")} onClick={() => trackCtaClick("hero", "request_quote")}>
                {t("cta.getQuoteHours")}
              </Link>
            </Button>
            <Button
              asChild
              size="lg"
              variant="whatsapp"
              className="h-12 w-full font-medium sm:w-auto"
            >
              <a {...whatsappLinkProps("hero")}>
                <MessageCircle className="mr-2 h-4 w-4" />
                {t("cta.sendWhatsapp")}
              </a>
            </Button>
          </div>
          <p className="mt-2 text-xs font-medium leading-relaxed text-muted-foreground sm:text-sm">
            {t("hero.proofLine")}
          </p>
          <p className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs leading-relaxed text-muted-foreground">
            <span>{t("hero.microNoAccount")}</span>
            <span aria-hidden="true">·</span>
            <span>{t("hero.microResponse")}</span>
          </p>

          {/* Differentiation line — moved below CTAs to reduce above-fold load */}
          <p className="mt-6 text-sm font-semibold text-foreground sm:text-base">
            {t("hero.differentiator")}
          </p>

          {/* Bullets */}
          <ul className="mt-6 grid gap-2.5 sm:grid-cols-2 sm:gap-3">
            {bulletKeys.map((k) => (
              <li
                key={k}
                className="flex items-start gap-2.5 text-sm text-foreground"
              >
                <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground">
                  <Check className="h-3 w-3" strokeWidth={3} />
                </span>
                <span className="font-medium">{t(k)}</span>
              </li>
            ))}
          </ul>

          {/* Subtle trust micro line — low visual weight, no new components */}
          <p className="mt-3 text-xs leading-relaxed text-muted-foreground">
            {t("hero.trustMicro")}
          </p>

          {/* Best for — moved below the fold */}
          <div className="mt-6 flex flex-wrap items-center gap-x-2 gap-y-1.5 text-sm">
            <span className="font-semibold text-foreground">{t("hero.bestFor.label")}</span>
            <span className="inline-flex items-center rounded-full border border-border bg-secondary px-2.5 py-0.5 text-xs font-medium text-foreground">
              {t("hero.bestFor.1")}
            </span>
            <span className="inline-flex items-center rounded-full border border-border bg-secondary px-2.5 py-0.5 text-xs font-medium text-foreground">
              {t("hero.bestFor.2")}
            </span>
            <span className="inline-flex items-center rounded-full border border-border bg-secondary px-2.5 py-0.5 text-xs font-medium text-foreground">
              {t("hero.bestFor.3")}
            </span>
          </div>

          {/* Highlight */}
          <div className="mt-6 flex items-center gap-3 rounded-lg bg-secondary px-4 py-3">
            <Clock className="h-5 w-5 shrink-0 text-foreground" />
            <p className="text-sm font-medium text-foreground">
              {t("hero.delivery")}{" "}
              <span className="font-bold">{t("hero.deliveryRange")}</span>
            </p>
          </div>
        </div>

        {/* Right: image carousel */}
        <div
          className="relative lg:pl-4"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
        >
          {/* Soft brand-tinted backdrop + faint grid (very low opacity, masked) */}
          <div className="absolute -inset-4 -z-10 rounded-3xl bg-gradient-to-tr from-primary-tint via-secondary to-transparent" />
          <div
            aria-hidden="true"
            className="bg-tech-grid bg-tech-grid-fade pointer-events-none absolute -inset-4 -z-10 rounded-3xl opacity-40"
          />
          <div
            className="relative aspect-square overflow-hidden rounded-2xl border border-border shadow-xl"
            role="region"
            aria-roledescription="carousel"
            aria-label="Switchgear categories we source"
          >
            {slides.map((s, i) => (
              <picture
                key={s.src}
                aria-hidden={i === active ? "false" : "true"}
                className={cn(
                  "absolute inset-0 transition-opacity duration-700 ease-out motion-reduce:transition-none",
                  i === active ? "opacity-100" : "opacity-0",
                )}
              >
                <source type="image/webp" srcSet={s.webpSrcSet} sizes={HERO_SIZES} />
                <source type="image/jpeg" srcSet={s.jpgSrcSet} sizes={HERO_SIZES} />
                <img
                  src={s.src}
                  alt={t(s.altKey)}
                  width={800}
                  height={800}
                  loading={i === 0 ? "eager" : "lazy"}
                  fetchPriority={i === 0 ? "high" : "auto"}
                  decoding={i === 0 ? "sync" : "async"}
                  className="absolute inset-0 h-full w-full object-cover"
                />
              </picture>
            ))}

            {/* Subtle gradient overlays for legibility */}
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-black/55 to-transparent sm:h-36"
            />
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-black/55 to-transparent"
            />

            {/* Top-left: eyebrow + slide title */}
            <div className="pointer-events-none absolute inset-x-4 top-4 sm:inset-x-5 sm:top-5">
              <span className="block text-[10px] font-semibold uppercase tracking-[0.2em] text-white/90 sm:text-xs">
                {t("hero.carousel.eyebrow")}
              </span>
              <h2
                key={slides[active].src}
                className="enter-fade mt-1.5 text-lg font-semibold leading-tight text-white drop-shadow sm:text-xl md:text-2xl"
              >
                {t(slides[active].captionKey)}
              </h2>
            </div>

            {/* Bottom-left info stack */}
            <div className="absolute left-4 bottom-4 flex flex-col gap-2 sm:left-5 sm:bottom-5">
              <span className="inline-flex w-fit items-center gap-1.5 rounded-full bg-primary px-2.5 py-1 text-[11px] font-semibold text-primary-foreground shadow-sm">
                <Clock className="h-3 w-3" />
                {t("hero.avgResponse")} {t("hero.avgResponseValue")}
              </span>
              <div className="w-fit rounded-md bg-background/95 px-3 py-1.5 shadow-sm backdrop-blur-sm">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  {t("hero.avgResponse")}
                </p>
                <p className="text-sm font-bold text-foreground">
                  {t("hero.avgResponseValue")}
                </p>
              </div>
            </div>

            {/* Dots */}
            <div className="absolute bottom-3 right-3 flex gap-0.5 sm:bottom-4 sm:right-4">
              {slides.map((s, i) => (
                <button
                  key={s.src}
                  type="button"
                  onClick={() => setActive(i)}
                  aria-label={`Show slide ${i + 1}: ${t(s.captionKey)}`}
                  aria-current={i === active}
                  className="group/dot inline-flex h-6 w-6 items-center justify-center"
                >
                  <span
                    aria-hidden="true"
                    className={cn(
                      "h-1.5 rounded-full bg-background/70 transition-all duration-300",
                      i === active ? "w-6 bg-background" : "w-1.5 group-hover/dot:bg-background",
                    )}
                  />
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
